import {
	columnVisibilityFeature,
	createColumnHelper,
	metaHelper,
	rowSortingFeature,
	tableFeatures,
} from '@tanstack/react-table';

import type { AssetUserType } from '@/common/asset';
import { ROLE } from '@/common/role';
import { DataTableColumnHeader } from '@/components/data-table/data-table-column-header';
import type { DataTableColumnMeta } from '@/components/data-table/data-table-features';
import type { SortFieldMap } from '@/components/data-table/sort-param';
import { DeleteButton } from '@/components/DeleteButton';
import { Restricted } from '@/components/Restricted';
import { Checkbox } from '@/components/ui/checkbox';
import type { BulkSelection } from '@/hooks/use-bulk-selection';
import { t } from '@/i18n';

export interface AssetUserTableMeta {
	onDeleteUser: (assetUser: AssetUserType) => void;
	/** Bulk-remove selection; the `select` column is hidden without it. */
	selection?: BulkSelection;
}

// Sorting is server-side (`manualSorting`), so no sorted row model or sort fns are registered.
export const assetUserTableFeatures = tableFeatures({
	columnVisibilityFeature,
	rowSortingFeature,
	tableMeta: metaHelper<AssetUserTableMeta>(),
	columnMeta: metaHelper<DataTableColumnMeta>(),
});

// Column id → backend `sort` field(s). Must match ASSET_USER_SORT_FIELDS in the backend DTO.
// Name sorts by first then last name, like the admin users list.
export const ASSET_USER_SORT_FIELDS: SortFieldMap = {
	name: ['user.firstName', 'user.lastName'],
	email: 'user.email',
};

/** `name` is empty for users created through the API, so fall back to first + last name. */
export function getAssetUserDisplayName(assetUser: AssetUserType): string | undefined {
	const user = assetUser.user;
	if (user?.name) return user.name;
	if (user?.firstName && user?.lastName) return `${user.firstName} ${user.lastName}`;
	return undefined;
}

const columnHelper = createColumnHelper<typeof assetUserTableFeatures, AssetUserType>();

export const assetUserColumns = columnHelper.columns([
	columnHelper.display({
		id: 'select',
		header: ({ table }) => {
			const selection = table.options.meta?.selection;
			if (!selection) return null;
			return (
				<Checkbox
					checked={selection.allSelected ? true : selection.someSelected ? 'indeterminate' : false}
					onCheckedChange={selection.toggleAll}
					aria-label={t('assets.users.selectAll')}
				/>
			);
		},
		cell: ({ row, table }) => {
			const selection = table.options.meta?.selection;
			if (!selection) return null;
			const id = row.original.id as string;
			return (
				<Checkbox
					checked={selection.isSelected(id)}
					onCheckedChange={() => selection.toggle(id)}
					aria-label={t('assets.users.selectAriaLabel', {
						name: getAssetUserDisplayName(row.original) ?? t('common.entities.user', { count: 1 }),
					})}
				/>
			);
		},
		meta: { className: 'py-2', headerClassName: 'w-10' },
	}),
	columnHelper.accessor((row) => getAssetUserDisplayName(row), {
		id: 'name',
		header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.labels.name')} />,
		cell: ({ getValue }) => <span className="font-medium">{getValue() ?? t('assets.users.unknownUser')}</span>,
	}),
	columnHelper.accessor((row) => row.user?.email, {
		id: 'email',
		header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.labels.email')} />,
		cell: ({ getValue }) => <span className="text-muted-foreground">{getValue()}</span>,
	}),
	columnHelper.display({
		id: 'actions',
		header: () => <span className="sr-only">{t('common.labels.actions')}</span>,
		cell: ({ row, table }) => (
			<Restricted to={ROLE.ASSET_USERS_DELETE}>
				<DeleteButton
					iconOnly
					label={t('assets.users.removeUser')}
					onClick={() => table.options.meta?.onDeleteUser(row.original)}
				/>
			</Restricted>
		),
		meta: { className: 'py-2 text-right', headerClassName: 'w-14' },
	}),
]);
